import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'

import { useTableSearch } from '@ui/v2/Table/hooks/useTableSearch'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'

import { IntroducingBrokerReportDto } from '@model/IntroducingBrokerReportDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { IBReportResultsPage, SearchResponseProps } from '../IBReportResultsPage'
import { IBReportsReportTable, IBReportsTableSortKeys } from './IBReportsReportTable'
import { ReportColumnCtx, reportColumns } from './IBReportsReportTable.columns'

const useReportsSearch = () =>
  useTableSearch<IBReportsTableSortKeys, IntroducingBrokerReportDto, ReportColumnCtx>({
    columns: reportColumns,
    placeholderKey: 'IB.Name or account number',
  })

const useReportsGenerateDocumentFile = (data: IntroducingBrokerReportDto[]) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatNumber, formatMoney } = useFormatNumber()

  const tableBody = () => {
    const headerCsvData = [
      [
        t('Trading Account.Trading Account'),
        t('IB.Client Name'),
        t('IB.Email'),
        t('Account Type'),
        t('IB.Registration Date'),
        t('Status'),
        t('Trading Account.Balance'),
        t('IB.Volume Since 2019'),
        t('IB.Volume Current Month'),
        t('IB.Volume Previous Month'),
        t('IB.Swap Free'),
        t('IB.Entity'),
        t('IB.Currency'),
        t('IB.Country'),
      ],
    ]

    return data.reduce((previousValue, currentValue) => {
      return previousValue.concat([
        [
          currentValue.login.toString(),
          currentValue.clientName,
          currentValue.email,
          currentValue.accountType,
          formatDate(currentValue.registrationDate, { formatType: dateFormat }),
          currentValue.status,
          formatMoney(currentValue.balance, currentValue.currency),
          formatNumber(currentValue.volumeSince2019),
          formatNumber(currentValue.volumeCurrentMonth),
          formatNumber(currentValue.volumePreviousMonth),
          currentValue.swapFree ? t('Yes') : t('No'),
          currentValue.clientEntity,
          currentValue.currency,
          currentValue.country,
        ],
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('reports_report', tableBody())
}

const useIntroducingBrokerReportsFetch = ({ search }: { search: SearchResponseProps }) => {
  const { account } = useAccountReadContext()
  const ibId = account?.clientIntroducingBroker?.id ?? ''
  const apiClient = useApiClient(ClientApiClient)

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.reports({ search: search.search }),
    queryFn: (pageQuery) =>
      apiClient.getIBReports(ibId, {
        ...pageQuery,
        search: search.search,
      }),
  })

  return { query }
}

export const IBReportsPage: React.FC = () => {
  const { t } = useTranslation()

  const search = useReportsSearch()
  const introducingBrokerReports = useIntroducingBrokerReportsFetch({ search })
  const generateDocumentFile = useReportsGenerateDocumentFile(introducingBrokerReports.query.data)

  return (
    <IBReportResultsPage
      title={t('Tabs.IB Reports')}
      data={introducingBrokerReports}
      generateDocumentFile={generateDocumentFile}
      scrollToHeaderId={ScrollToIds.IBSignupHeader}
      table={<IBReportsReportTable data={introducingBrokerReports} search={search} />}
    />
  )
}
